import { assertNever } from './exhaustive';
import {
  type FieldType,
  type FieldTypeOptions,
  type FilterHandlerMap,
  type FilterOperator,
  type SelectOption,
  supportsOperator,
} from './field-types';
import type { FieldCondition, FilterNode } from './filter-ast';
import { type NormalizedCondition, normalizeCondition, type Operand } from './filter-operand';

/**
 * INV-6 ONE FILTER SEMANTICS, the JS backend: the same AST, evaluated against a
 * record's LOGICAL values (`RecordResult.values`), in the browser.
 *
 * It exists so an optimistic update can decide "does this edited record still
 * belong in this view" without a round trip. The SQL compiler is the authority; this
 * one has to AGREE with it, which is what the shared conformance corpus proves.
 *
 * ## The semantics, stated once (the corpus is the executable version)
 *
 * PRESENCE. Every operator except `isEmpty` requires the field to be present.
 * `isNotEmpty` is exactly `not isEmpty`. Present means:
 *   - text family: a non-null, non-empty string. PHONE is present when the ORIGINAL
 *     spelling is there, even if it could not be parsed - a raw "0151 (Mama)" is a
 *     value the user entered, not an empty field.
 *   - MULTI_SELECT: a non-null array with at least one element. `[]` is empty.
 *   - everything else: not null. `false` and `0` are present.
 *
 * NULL. A missing value matches NOTHING but `isEmpty` - including `neq` and
 * `notIn`. "Status is not WON" therefore does not smuggle in records with no status;
 * ask for that explicitly with `or(neq(WON), isEmpty)`. The escape hatch for a true
 * set complement is `not(...)` on a group.
 *
 * CASE. `contains`, `notContains`, `startsWith`, `endsWith` are case-INSENSITIVE;
 * `eq`, `neq`, `in`, `notIn` are case-SENSITIVE. Folding is `toLowerCase()` here and
 * `lower()` in PostgreSQL. Those agree across Latin-1, German (including ß) and CJK,
 * which the corpus asserts; they do NOT agree on Unicode special-casing such as
 * U+0130 LATIN CAPITAL LETTER I WITH DOT ABOVE, which is out of scope and excluded
 * from the corpus rather than quietly assumed.
 *
 * WILDCARDS. `%` and `_` in an operand are literal text, never patterns.
 *
 * EMPTY SETS. `in []` and `containsAny []` and `contains []` match nothing;
 * `notIn []` matches every present row. Both backends spell this out rather than
 * inheriting whatever PostgreSQL's array operators do with an empty array.
 *
 * PHONE. Comparisons target the CANONICAL E.164 column (that is the column identity
 * resolution uses). A phone whose input could not be parsed has no canonical form,
 * so it is present but matches no comparison. Finding a number the way a user typed
 * it is what `/api/search` is for: the search vector indexes the original spelling.
 *
 * CURRENCY. Comparisons target `amount_micros`. An operand may pin a currency
 * (`{amountMicros, currencyCode}`), which adds an equality on the code column; without
 * one, amounts of different currencies are compared as bare numbers, because the
 * alternative is inventing an exchange rate.
 */

export interface EvaluatorField {
  readonly type: FieldType;
  /** SELECT / MULTI_SELECT options, when known: lets bad option ids be rejected. */
  readonly options?: readonly SelectOption[] | undefined;
}

export interface EvaluationContext {
  /** Field id -> its type. Same map shape the permission and validation paths use. */
  readonly fields: ReadonlyMap<string, EvaluatorField>;
  /** Logical values by field id, exactly as `RecordResult.values` carries them. */
  readonly values: Readonly<Record<string, unknown>>;
}

export class FilterEvaluationError extends Error {
  override readonly name = 'FilterEvaluationError';
}

// --- value readers ----------------------------------------------------------

/** The string a text-family comparison runs against, or null when there is none. */
function textTarget(fieldType: FieldType, stored: unknown): string | null {
  if (fieldType === 'PHONE') {
    if (typeof stored !== 'object' || stored === null) return null;
    const canonical = (stored as { canonical?: unknown }).canonical;
    return typeof canonical === 'string' && canonical !== '' ? canonical : null;
  }
  return typeof stored === 'string' && stored !== '' ? stored : null;
}

function textPresent(fieldType: FieldType, stored: unknown): boolean {
  if (fieldType === 'PHONE') {
    if (typeof stored !== 'object' || stored === null) return false;
    const original = (stored as { original?: unknown }).original;
    return typeof original === 'string' && original.trim() !== '';
  }
  return typeof stored === 'string' && stored !== '';
}

function numberTarget(fieldType: FieldType, stored: unknown): number | null {
  if (fieldType === 'CURRENCY') {
    if (typeof stored !== 'object' || stored === null) return null;
    const amount = (stored as { amountMicros?: unknown }).amountMicros;
    return typeof amount === 'number' ? amount : null;
  }
  return typeof stored === 'number' ? stored : null;
}

function currencyCodeOf(stored: unknown): string | null {
  if (typeof stored !== 'object' || stored === null) return null;
  const code = (stored as { currencyCode?: unknown }).currencyCode;
  return typeof code === 'string' ? code : null;
}

function optionArray(stored: unknown): readonly string[] | null {
  if (!Array.isArray(stored)) return null;
  return stored.filter((item): item is string => typeof item === 'string');
}

function fold(value: string): string {
  return value.toLowerCase();
}

// --- handlers ---------------------------------------------------------------

/**
 * One predicate over one stored logical value. `stored` is `undefined` when the
 * record carries no entry for the field at all, which is the same as null.
 */
type Handler = (stored: unknown, condition: NormalizedCondition) => boolean;

function text(operand: Operand): string {
  if (operand.kind !== 'text') throw new FilterEvaluationError(`expected a text operand`);
  return operand.value;
}

function textList(operand: Operand): readonly string[] {
  if (operand.kind !== 'textList') throw new FilterEvaluationError('expected a text list operand');
  return operand.values;
}

function num(operand: Operand): number {
  if (operand.kind !== 'number') throw new FilterEvaluationError('expected a number operand');
  return operand.value;
}

function numRange(operand: Operand): { readonly lo: number; readonly hi: number } {
  if (operand.kind !== 'numberRange') {
    throw new FilterEvaluationError('expected a number range operand');
  }
  return operand;
}

function optionId(operand: Operand): string {
  if (operand.kind !== 'option') throw new FilterEvaluationError('expected an option operand');
  return operand.value;
}

function optionIds(operand: Operand): readonly string[] {
  if (operand.kind !== 'optionList') {
    throw new FilterEvaluationError('expected an option list operand');
  }
  return operand.values;
}

function id(operand: Operand): string {
  if (operand.kind !== 'id') throw new FilterEvaluationError('expected an id operand');
  return operand.value;
}

function ids(operand: Operand): readonly string[] {
  if (operand.kind !== 'idList') throw new FilterEvaluationError('expected an id list operand');
  return operand.values;
}

/** Wraps a comparison so the presence rule is applied in exactly one place. */
function present(
  isPresent: (fieldType: FieldType, stored: unknown) => boolean,
  compare: Handler,
): Handler {
  return (stored, condition) =>
    isPresent(condition.fieldType, stored) && compare(stored, condition);
}

const notNull = (_fieldType: FieldType, stored: unknown): boolean =>
  stored !== null && stored !== undefined;

/** CURRENCY is present when its AMOUNT is, which is the column `is null` reads in SQL. */
const numberPresent = (fieldType: FieldType, stored: unknown): boolean =>
  numberTarget(fieldType, stored) !== null;

const multiSelectPresent = (_fieldType: FieldType, stored: unknown): boolean => {
  const array = optionArray(stored);
  return array !== null && array.length > 0;
};

/**
 * FILES (`file-attachments.md` D10, D11): an attachment list is empty when it is null or
 * when it holds nothing - the JS twin of `col is null or jsonb_array_length(col) = 0`.
 * Same shape as MULTI_SELECT's and deliberately its own function: the two arrays hold
 * different things, and one day one of them will need to look inside.
 */
const filesPresent = (_fieldType: FieldType, stored: unknown): boolean =>
  Array.isArray(stored) && stored.length > 0;

/**
 * WHAT "EMPTY" MEANS, PER FIELD TYPE, IN ONE TABLE - the JS half of the same table
 * `filter-sql.ts` holds for PostgreSQL.
 *
 * `satisfies Record<FieldType, ...>` makes it total: a thirteenth field type does not
 * compile until it says what an empty one looks like.
 *
 * It is EXPORTED because the fixture data client's `countEmpty` counts with it
 * (`FixtureClient.ts`, and `docs/decisions/group-by-aggregation.md` D12, register 18): a
 * footer that says "3 empty" beside a filter chip that finds 5 is the defect the grouping
 * module exists to prevent, and one table per backend is what keeps them from drifting.
 */
const PRESENCE_BY_TYPE = {
  TEXT: textPresent,
  EMAIL: textPresent,
  URL: textPresent,
  PHONE: textPresent,
  NUMBER: numberPresent,
  CURRENCY: numberPresent,
  BOOLEAN: notNull,
  DATE: notNull,
  DATETIME: notNull,
  SELECT: notNull,
  MULTI_SELECT: multiSelectPresent,
  RELATION: notNull,
  FILES: filesPresent,
} satisfies Record<FieldType, (fieldType: FieldType, stored: unknown) => boolean>;

/** "This field has a value". `isNotEmpty` is exactly this. */
export function isFieldValuePresent(fieldType: FieldType, stored: unknown): boolean {
  return PRESENCE_BY_TYPE[fieldType](fieldType, stored);
}

/** "This field has no value". `isEmpty` is exactly this. */
export function isFieldValueEmpty(fieldType: FieldType, stored: unknown): boolean {
  return !isFieldValuePresent(fieldType, stored);
}

/**
 * The two presence OPERATORS, for every family, off the one table. Spread into each
 * handler set rather than restated per family, so no family can answer `isEmpty`
 * differently from `isFieldValueEmpty` - which is what the aggregate counts with.
 */
const presenceHandlers = {
  isEmpty: (stored, condition) => isFieldValueEmpty(condition.fieldType, stored),
  isNotEmpty: (stored, condition) => isFieldValuePresent(condition.fieldType, stored),
} satisfies Record<string, Handler>;

/** Comparison against the text target; absent target => false for every operator. */
function onText(compare: (target: string, condition: NormalizedCondition) => boolean): Handler {
  return present(textPresent, (stored, condition) => {
    const target = textTarget(condition.fieldType, stored);
    return target !== null && compare(target, condition);
  });
}

const textualHandlers = {
  eq: onText((target, condition) => target === text(condition.operand)),
  neq: onText((target, condition) => target !== text(condition.operand)),
  contains: onText((target, condition) => fold(target).includes(fold(text(condition.operand)))),
  notContains: onText((target, condition) => !fold(target).includes(fold(text(condition.operand)))),
  startsWith: onText((target, condition) => fold(target).startsWith(fold(text(condition.operand)))),
  endsWith: onText((target, condition) => fold(target).endsWith(fold(text(condition.operand)))),
  in: onText((target, condition) => textList(condition.operand).includes(target)),
  notIn: onText((target, condition) => !textList(condition.operand).includes(target)),
  ...presenceHandlers,
} satisfies Record<string, Handler>;

/** Comparison against the numeric target, plus the optional currency pin. */
function onNumber(compare: (target: number, condition: NormalizedCondition) => boolean): Handler {
  return present(notNull, (stored, condition) => {
    const target = numberTarget(condition.fieldType, stored);
    if (target === null) return false;
    if (condition.currencyCode !== undefined && currencyCodeOf(stored) !== condition.currencyCode) {
      return false;
    }
    return compare(target, condition);
  });
}

const numericHandlers = {
  eq: onNumber((target, condition) => target === num(condition.operand)),
  neq: onNumber((target, condition) => target !== num(condition.operand)),
  lt: onNumber((target, condition) => target < num(condition.operand)),
  lte: onNumber((target, condition) => target <= num(condition.operand)),
  gt: onNumber((target, condition) => target > num(condition.operand)),
  gte: onNumber((target, condition) => target >= num(condition.operand)),
  between: onNumber((target, condition) => {
    const range = numRange(condition.operand);
    return target >= range.lo && target <= range.hi;
  }),
  ...presenceHandlers,
} satisfies Record<string, Handler>;

const booleanHandlers = {
  eq: present(notNull, (stored, condition) => {
    if (condition.operand.kind !== 'boolean') return false;
    return stored === condition.operand.value;
  }),
  neq: present(notNull, (stored, condition) => {
    if (condition.operand.kind !== 'boolean') return false;
    return stored !== condition.operand.value;
  }),
  ...presenceHandlers,
} satisfies Record<string, Handler>;

/**
 * DATE compares as TEXT. `YYYY-MM-DD` sorts chronologically as a string, and a
 * calendar date has no timezone: turning it into a Date would introduce the very
 * local-midnight shift `db/client.ts` disables the driver's date parser to avoid.
 */
function onDate(compare: (target: string, condition: NormalizedCondition) => boolean): Handler {
  return present(
    notNull,
    (stored, condition) => typeof stored === 'string' && compare(stored, condition),
  );
}

function dateOperand(operand: Operand): string {
  if (operand.kind !== 'date') throw new FilterEvaluationError('expected a date operand');
  return operand.value;
}

function dateRange(operand: Operand): { readonly lo: string; readonly hi: string } {
  if (operand.kind !== 'dateRange')
    throw new FilterEvaluationError('expected a date range operand');
  return operand;
}

const dateHandlers = {
  eq: onDate((target, condition) => target === dateOperand(condition.operand)),
  neq: onDate((target, condition) => target !== dateOperand(condition.operand)),
  before: onDate((target, condition) => target < dateOperand(condition.operand)),
  after: onDate((target, condition) => target > dateOperand(condition.operand)),
  /**
   * The HALF-OPEN pair a date bucket's "show more" is built from
   * (`docs/decisions/group-by-aggregation.md` D19). `lt` is `before` under the generic
   * name; `gte` is the lower bound the temporal vocabulary had no way to express, and
   * `between` cannot stand in for it because it includes BOTH ends.
   */
  lt: onDate((target, condition) => target < dateOperand(condition.operand)),
  gte: onDate((target, condition) => target >= dateOperand(condition.operand)),
  between: onDate((target, condition) => {
    const range = dateRange(condition.operand);
    return target >= range.lo && target <= range.hi;
  }),
  ...presenceHandlers,
} satisfies Record<string, Handler>;

/**
 * DATETIME compares as an INSTANT, not as text. `2026-07-27T12:30:00+02:00` and
 * `2026-07-27T10:30:00Z` are the same moment, and PostgreSQL's timestamptz says so;
 * a string comparison here would disagree with the database for every offset that is
 * not Z.
 */
function instant(value: unknown): number | null {
  if (typeof value !== 'string') return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function onDatetime(compare: (target: number, operand: number) => boolean): Handler {
  return present(notNull, (stored, condition) => {
    const target = instant(stored);
    if (target === null) return false;
    if (condition.operand.kind !== 'datetime') return false;
    const operand = instant(condition.operand.value);
    return operand !== null && compare(target, operand);
  });
}

const datetimeHandlers = {
  eq: onDatetime((target, operand) => target === operand),
  neq: onDatetime((target, operand) => target !== operand),
  before: onDatetime((target, operand) => target < operand),
  after: onDatetime((target, operand) => target > operand),
  /** D19's half-open pair, on instants rather than on text (see `dateHandlers`). */
  lt: onDatetime((target, operand) => target < operand),
  gte: onDatetime((target, operand) => target >= operand),
  between: present(notNull, (stored, condition) => {
    const target = instant(stored);
    if (target === null || condition.operand.kind !== 'datetimeRange') return false;
    const lo = instant(condition.operand.lo);
    const hi = instant(condition.operand.hi);
    return lo !== null && hi !== null && target >= lo && target <= hi;
  }),
  ...presenceHandlers,
} satisfies Record<string, Handler>;

const selectHandlers = {
  eq: present(notNull, (stored, condition) => stored === optionId(condition.operand)),
  neq: present(notNull, (stored, condition) => stored !== optionId(condition.operand)),
  in: present(notNull, (stored, condition) =>
    optionIds(condition.operand).includes(String(stored)),
  ),
  notIn: present(
    notNull,
    (stored, condition) => !optionIds(condition.operand).includes(String(stored)),
  ),
  ...presenceHandlers,
} satisfies Record<string, Handler>;

const multiSelectHandlers = {
  /** Superset: the row carries every requested tag. An empty request matches nothing. */
  contains: present(multiSelectPresent, (stored, condition) => {
    const array = optionArray(stored) ?? [];
    const wanted = optionIds(condition.operand);
    return wanted.length > 0 && wanted.every((value) => array.includes(value));
  }),
  /** Overlap: the row carries at least one requested tag. */
  containsAny: present(multiSelectPresent, (stored, condition) => {
    const array = optionArray(stored) ?? [];
    return optionIds(condition.operand).some((value) => array.includes(value));
  }),
  ...presenceHandlers,
} satisfies Record<string, Handler>;

const relationHandlers = {
  eq: present(notNull, (stored, condition) => stored === id(condition.operand)),
  neq: present(notNull, (stored, condition) => stored !== id(condition.operand)),
  in: present(notNull, (stored, condition) => ids(condition.operand).includes(String(stored))),
  notIn: present(notNull, (stored, condition) => !ids(condition.operand).includes(String(stored))),
  ...presenceHandlers,
} satisfies Record<string, Handler>;

/**
 * INV-6's compile-time half. `satisfies FilterHandlerMap<Handler>` makes this table
 * total against the REGISTRY: an operator added to a field type's `filterOperators`
 * breaks this file until it is implemented, and so does the SQL compiler's twin
 * table. Neither backend can be the only one that knows an operator.
 */
export const JS_FILTER_BACKEND = {
  TEXT: textualHandlers,
  EMAIL: textualHandlers,
  URL: textualHandlers,
  PHONE: textualHandlers,
  NUMBER: numericHandlers,
  CURRENCY: numericHandlers,
  BOOLEAN: booleanHandlers,
  DATE: dateHandlers,
  DATETIME: datetimeHandlers,
  SELECT: selectHandlers,
  MULTI_SELECT: multiSelectHandlers,
  RELATION: relationHandlers,
  /**
   * FILES declares NOTHING BUT the presence pair (`file-attachments.md` D10), so its whole
   * handler set is the shared table above. Nothing to spread it into.
   */
  FILES: presenceHandlers,
} satisfies FilterHandlerMap<Handler>;

/**
 * Runtime dispatch into the table above.
 *
 * The one cast in this module: `FilterHandlerMap` is a mapped type whose per-type
 * key sets differ, and TypeScript cannot index it with an operator that is only
 * known to be legal at runtime. `supportsOperator` has already established that it
 * is - that check is not a formality here, it is what makes the cast sound.
 */
function handlerFor(fieldType: FieldType, operator: FilterOperator): Handler {
  if (!supportsOperator(fieldType, operator)) {
    throw new FilterEvaluationError(`operator ${operator} is not supported for ${fieldType}`);
  }
  const table = JS_FILTER_BACKEND[fieldType] as Readonly<Record<string, Handler | undefined>>;
  const handler = table[operator];
  if (handler === undefined) {
    throw new FilterEvaluationError(`no JS handler for ${fieldType}.${operator}`);
  }
  return handler;
}

function evaluateCondition(node: FieldCondition, ctx: EvaluationContext): boolean {
  const field = ctx.fields.get(node.fieldId);
  if (field === undefined) {
    throw new FilterEvaluationError(`unknown fieldId ${node.fieldId}`);
  }
  const options: FieldTypeOptions | undefined =
    field.options === undefined ? undefined : { selectOptions: field.options };
  const condition = normalizeCondition(field.type, node.operator, node.value, options);
  return handlerFor(field.type, node.operator)(ctx.values[node.fieldId], condition);
}

/**
 * Evaluates the AST against one record.
 *
 * An empty group is its combinator's neutral element: an empty AND matches (a filter
 * with no conditions filters nothing), an empty OR does not (there is no alternative
 * that could hold). Anything else would make building a filter incrementally in the
 * UI behave differently from the same filter sent to the server.
 */
export function evaluateFilter(node: FilterNode, ctx: EvaluationContext): boolean {
  switch (node.kind) {
    case 'condition':
      return evaluateCondition(node, ctx);
    case 'group': {
      const inner =
        node.combinator === 'and'
          ? node.children.every((child) => evaluateFilter(child, ctx))
          : node.children.some((child) => evaluateFilter(child, ctx));
      return node.negate === true ? !inner : inner;
    }
    /**
     * INV-6's 2026-08-25 amendment (D14). This evaluator's entire input is ONE record's
     * values - there is no context parameter, no async and no database - so a condition
     * that crosses a table cannot be answered here by any amount of engineering. It does
     * not fall through and it does not guess: it refuses BY NAME, the conformance corpus
     * asserts this throw for every related case, and each of the five production callers
     * already has a decided behaviour for a `FilterEvaluationError`.
     *
     * The refusal is read BEFORE the quantifier, which is why D28's third quantifier
     * costs the JS backend nothing.
     */
    case 'related':
      throw new FilterEvaluationError(
        `a related condition (anchor ${node.anchorFieldId}) asks about a second table and is evaluated by the SQL compiler only`,
      );
    default:
      return assertNever(node, 'evaluateFilter');
  }
}
